import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Heading,
  IconClubCatch,
  IconTick,
  Link,
  List,
  ListItem,
  LoadingSpinner,
  Modal,
  RadioGroup,
  Span,
  Text,
  useTheme,
} from "@catchoftheday/cg-components";
import { AddToCartInput } from "@cart-app/generated/graphql";
import { MutationType, useToast } from "@cart-app/hooks/useToast";
import { getSourcePlatform } from "@analytics/utilities";
import { formatPrice } from "@cart-app/utilities";
import {
  useClubCatchPlanSelectionModalLazyQuery,
  ClubOptionFragment,
} from "@common-vendors/generated/graphql";
import { emitAddToCart, trackLinkClick } from "@catchoftheday/analytics";
import { useEventBus } from "@catchoftheday/state";
import { withApollo } from "@catchoftheday/graphql-utils";
import { eventBus as eventBusJS } from "@header-app/utilities";
import { DEFAULT_CLUB_CATCH_EVENT_ID } from "@common-vendors/consts";
import { PlanOptionRadio } from "./PlanOptionRadio";
import { TRIGGER_CC_PLAN_MODAL_EVENT } from "./trigger";

type ClubCatchPlanSelectionModalProps = {
  /** setLoading to be passed into useToast callback */
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  /** setError to be passed into useToast callback */
  setError?: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const CLUB_CATCH_BENEFITS = [
  "Enjoy free delivery on eligible products",
  "Weekly exclusive deals",
  "1000's of member discounts",
  "Early alerts",
];

export const ClubCatchPlanSelectionModal = ({
  setLoading,
  setError,
}: ClubCatchPlanSelectionModalProps) => {
  const theme = useTheme();
  const [getData, { data, loading, error }] =
    useClubCatchPlanSelectionModalLazyQuery({
      fetchPolicy: "network-only",
    });
  const cartSelectedOption = data?.cart.clubSubscription.selectedOption;
  const [isOpen, setIsOpen] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [clubOption, setClubOption] = useState<ClubOptionFragment | null>(
    cartSelectedOption || null
  );
  const clubSubscriptionOptions = data?.cart.clubSubscription.options;

  const clubMonthlyOption = clubSubscriptionOptions?.find(
    ({ interval }) => interval == "MONTHLY"
  ) as ClubOptionFragment;

  const isLapsedClubMember = data?.customer?.club && !data.customer.club.active;

  const addClubToCart = useToast<AddToCartInput>({
    mutationType: MutationType.ADD_TO_CART,
    setLoading,
    setError,
    showCheckoutOnSuccess: false,
    showTickOnSuccess: true,
  });
  const onAddClubToCart = useCallback(async () => {
    if (clubOption) {
      setIsOpen(false);
      const { status } = await addClubToCart(
        { imageUrl: clubOption?.clubImage.url, title: clubOption?.title },
        {
          offerId: clubOption?.id,
          quantity: 1,
          sourcePlatform: getSourcePlatform(),
        }
      );
      if (status !== "ServerError") {
        emitAddToCart({
          customer_id: data?.customer?.id,
          success: status == "OK",
          offer_id: clubOption.id,
          event_id: DEFAULT_CLUB_CATCH_EVENT_ID,
        });
      }
    }
  }, [addClubToCart, clubOption, data?.customer?.id]);

  const handleRadioChange = (value: string) => {
    if (isOpen && clubSubscriptionOptions?.length) {
      const selectedOption = clubSubscriptionOptions?.find(
        (opt) => opt.id === value
      );
      setClubOption(selectedOption || null);
      setButtonDisabled(false);
    }
  };

  const handleModalClose = () => {
    setIsOpen(false);
    setButtonDisabled(true);
  };

  const handleModalCloseWithTracking = () => {
    trackLinkClick({
      targetUrl: window.location.href,
      elementClasses: ["close", "js-modal-close"],
    });
    handleModalClose();
  };

  const buttonText = cartSelectedOption
    ? "Update Cart"
    : isLapsedClubMember
    ? "Renew Membership"
    : "Start 30-day Free Trial";

  useEventBus(TRIGGER_CC_PLAN_MODAL_EVENT, () => {
    if (!data) {
      getData();
    }
    if (!loading && !error) {
      setIsOpen(true);
    }
  });

  useEffect(() => {
    eventBusJS?.on(TRIGGER_CC_PLAN_MODAL_EVENT.type, () => {
      getData();
      if (!loading && !error) {
        setIsOpen(true);
      }
    });
  }, [error, loading, data, getData]);

  useEffect(() => {
    if (!loading) {
      setClubOption(cartSelectedOption || clubMonthlyOption);
    }
  }, [loading, cartSelectedOption, clubMonthlyOption]);

  if (error) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      overflow="hidden"
      contentProps={{
        p: "0px",
        textAlign: "center",
        position: "relative",
      }}
      onRequestClose={handleModalClose}
      onCloseBtnClick={handleModalCloseWithTracking}
      onAfterOpen={() => {
        if (!loading) {
          setClubOption(cartSelectedOption || clubMonthlyOption);
        }
      }}
      maxWidth="500px"
      marginX="10px"
      height={{
        xs: "auto",
      }}
      zIndex={101}
    >
      {loading && (
        <LoadingSpinner
          containerProps={{ "aria-label": "loading spinner", zIndex: 1 }}
          hasOverlay
          ringBg="ui.borderColor"
        />
      )}
      <Box
        background={`linear-gradient(64.7deg, ${theme.colors.brand.clubCatchLight} 16.05%, ${theme.colors.brand.clubCatch} 83.95%)`}
        textAlign="center"
        py="20px"
        mb="20px"
        color="textWhite"
      >
        <IconClubCatch
          aria-label="Club Catch"
          height={{
            xs: "33px",
            md: "50px",
          }}
          width="auto"
          color="inherit"
        />
      </Box>
      {!!cartSelectedOption && (
        <Alert
          type="inline"
          variant="info"
          wrapperProps={{
            px: "14px",
            mb: "16px",
          }}
          fontSize={{
            xs: "xs",
            md: "sm",
          }}
          textAlign="left"
        >
          Your already have a Club Catch membership in your cart
        </Alert>
      )}
      <Box
        px={{
          xs: "12px",
          md: "20px",
        }}
      >
        <Heading.h2
          textAlign="center"
          color="brand.clubCatch"
          m={0}
          mb="14px"
          fontSize={{
            xs: "lg",
            md: "2xl",
          }}
        >
          {isLapsedClubMember
            ? "Renew your membership"
            : "Try Club Catch, FREE for 30 days!"}
        </Heading.h2>
        <List maxWidth="340px" margin="0 auto" aria-label="Club Catch benefits">
          {CLUB_CATCH_BENEFITS.map((benefit, idx) => (
            <ListItem
              key={`club-benefit-${idx}`}
              display="flex"
              alignItems="center"
              my="6px"
            >
              <IconTick mx="14px" height="12px" width="auto" color="primary" />
              <Span fontSize="base">{benefit}</Span>
            </ListItem>
          ))}
        </List>
        <Heading.h3
          textAlign="center"
          color="brand.clubCatch"
          my="16px"
          fontSize={{
            xs: "lg",
            md: "xl",
          }}
        >
          Choose your {isLapsedClubMember ? "plan" : "membership"}
        </Heading.h3>
        <RadioGroup
          justifyContent="center"
          alignItems="center"
          name="clubCatchOption"
          display="flex"
          flexDirection="row"
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            handleRadioChange(e.currentTarget.value)
          }
          value={clubOption?.id || ""}
        >
          {clubSubscriptionOptions?.map((option) => (
            <PlanOptionRadio
              key={option.id}
              clubSubscriptionOption={option}
              selected={clubOption?.id === option.id}
              onChange={(value) => handleRadioChange(value)}
            />
          ))}
        </RadioGroup>
        <Button
          variant="primary"
          my="16px"
          disabled={!clubOption?.id || (buttonDisabled && !!cartSelectedOption)}
          onClick={onAddClubToCart}
          width={{
            xs: "100%",
            lg: "auto",
          }}
        >
          {buttonText}
        </Button>
        {!data?.customer && (
          <Text mt={0} fontSize="base">
            <Span fontSize="inherit">Already a member?</Span>{" "}
            <Link
              fontSize="inherit"
              href="/login"
              data-tracking="club-catch-plan-selection-modal-login"
            >
              Login
            </Link>
          </Text>
        )}
        <Text mt={0}>
          <Span>
            {!isLapsedClubMember && "After your 30-day free trial, "}
            Club Catch is only{" "}
            {formatPrice(
              clubOption
                ? clubOption.price.amount
                : clubMonthlyOption?.price?.amount
            )}
            {clubOption?.interval === "YEARLY" ? "/year" : "/month"}, cancel{" "}
            anytime.
          </Span>{" "}
          <Span whiteSpace="nowrap">
            <Link href="/club-catch">Learn More</Link> |{" "}
            <Link href="/club-catch-terms">Terms &amp; Conditions</Link>
          </Span>
        </Text>
      </Box>
    </Modal>
  );
};

export const ClubCatchPlanSelectionModalWithApollo = withApollo({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
  cacheConfig: {
    typePolicies: {
      CartClubSubscriptionOption: {
        keyFields: ["id", "price", ["amount"]],
      },
    },
  },
})(ClubCatchPlanSelectionModal);
